import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CodigoCopiable from "@/ui/CodigoCopiable";
import ConfirmDialog from "@/ui/ConfirmDialog";
import AyudaVista from "@/ui/AyudaVista";
import Modal from "@/ui/Modal";
import { ayudaVentas } from "@/components/ayuda/vistas/ventas";
import { useToast } from "@/ui/Toast";
import { fmtBytes } from "@/lib/formato";

/**
 * Catálogo de equipos vendidos — el Excel mensual del CRM, buscable.
 *
 * Para qué existe: soporte solo atiende equipos vendidos por Emprenet, pero
 * no había forma rápida de comprobarlo y "nos metían goles". Aquí buscas un
 * IMEI, empresa o cotización y en segundos sabes si lo vendimos, a quién,
 * cuándo y si ya tuvo garantías.
 *
 * La fuente de verdad es el CRM: aquí no se captura nada a mano; el admin
 * sube el Excel cuando llega y la carga es idempotente (re-subir el
 * histórico completo no duplica).
 */

// Tipos locales: esta vista es su único consumidor. (Cuando se escribió, lib/api
// era un solo archivo pasado de líneas; desde 2026-10-13 es `lib/api/` por tema.)
type IdentificadorOut = {
  tipo: "imei" | "iccid" | "serial" | "otro";
  valor: string;
  rol: "normal" | "falla" | "nuevo";
};

type VentaOut = {
  id: number;
  fecha_cierre: string | null;
  nombre: string | null;
  apellidos: string | null;
  empresa: string | null;
  concepto: string | null;
  cotizacion: string | null;
  es_garantia: boolean;
  observaciones: string | null;
  identificadores: IdentificadorOut[];
};

type VentasPage = { total: number; items: VentaOut[] };

type VentasResumen = {
  total_ventas: number;
  total_equipos: number;
  fecha_min: string | null;
  fecha_max: string | null;
  ultima_carga_at: string | null;
};

type CargaOut = {
  id: number;
  filename: string;
  subido_por: string;
  created_at: string;
  filas_total: number;
  filas_nuevas: number;
  filas_duplicadas: number;
  filas_avisos: number;
  identificadores_nuevos: number;
};

type CargaResumen = {
  carga_id: number;
  filas_total: number;
  filas_nuevas: number;
  filas_duplicadas: number;
  filas_avisos: number;
  identificadores_nuevos: number;
  avisos_detalle: string[];
};

const PAGE_SIZE = 25;

const fmtFecha = new Intl.DateTimeFormat("es-MX", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

function fecha(iso: string | null): string {
  if (!iso) return "—";
  return fmtFecha.format(new Date(`${iso}T00:00:00`));
}

function fechaMes(iso: string | null): string {
  if (!iso) return "?";
  return new Intl.DateTimeFormat("es-MX", { month: "short", year: "numeric" }).format(
    new Date(`${iso}T00:00:00`),
  );
}

function contacto(v: VentaOut): string {
  return [v.nombre, v.apellidos].filter(Boolean).join(" ") || "—";
}

/** Equipos físicos de la venta: IMEIs (GPS) + seriales (cámaras LIGO/SP).
 * Los ICCID son SIMs y «otro» son referencias (OC, pedimento) — no equipos. */
function contarEquipos(v: VentaOut): number {
  return v.identificadores.filter((i) => i.tipo === "imei" || i.tipo === "serial").length;
}

/** "hace 3 días" para la última carga. */
function hace(iso: string | null): string | null {
  if (!iso) return null;
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (dias <= 0) return "hoy";
  if (dias === 1) return "ayer";
  if (dias < 30) return `hace ${dias} días`;
  const meses = Math.round(dias / 30);
  return `hace ${meses} mes${meses === 1 ? "" : "es"}`;
}

export default function Ventas() {
  const me = useAuth((s) => s.user);
  const esAdmin = me?.rol === "admin";

  // q vive en la URL: una búsqueda se puede compartir por link.
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") ?? "");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<VentasPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [resumen, setResumen] = useState<VentasResumen | null>(null);
  const [expandida, setExpandida] = useState<number | null>(null);
  const [subirAbierto, setSubirAbierto] = useState(false);
  const [cargasAbierto, setCargasAbierto] = useState(false);

  const cargarResumen = useCallback(async () => {
    try {
      setResumen(await api.get<VentasResumen>("/api/ventas/resumen"));
    } catch {
      // La línea de contexto es informativa; si falla no bloquea la vista.
    }
  }, []);

  const cargar = useCallback(
    async (busqueda: string, pagina: number) => {
      setLoading(true);
      setErrorCarga(false);
      try {
        const params = new URLSearchParams({ page: String(pagina), page_size: String(PAGE_SIZE) });
        if (busqueda.trim()) params.set("q", busqueda.trim());
        setData(await api.get<VentasPage>(`/api/ventas?${params}`));
        setExpandida(null);
      } catch {
        setErrorCarga(true);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void cargarResumen();
  }, [cargarResumen]);

  // Debounce de la búsqueda + sync del querystring.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      void cargar(q, 1);
      setSearchParams(q.trim() ? { q: q.trim() } : {}, { replace: true });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  function irAPagina(p: number) {
    setPage(p);
    void cargar(q, p);
  }

  async function alTerminarCarga() {
    await Promise.all([cargar(q, page), cargarResumen()]);
  }

  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  // El match resaltado en los chips: la búsqueda compactada (un IMEI pegado
  // con espacios/saltos sigue resaltando su chip).
  const qCompacta = q.replace(/\s+/g, "").toLowerCase();

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-xl font-semibold">Ventas</h1>
            <p className="text-sm text-zinc-400">
              Verifica si un equipo lo vendimos: a quién, cuándo y con qué cotización.
            </p>
          </div>
          <AyudaVista titulo="Ventas" {...ayudaVentas({ esAdmin })} />
        </div>
        {esAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={() => setCargasAbierto(true)} className="btn-ghost">
              Cargas
            </button>
            <button
              onClick={() => setSubirAbierto(true)}
              className="btn-primary whitespace-nowrap"
            >
              Subir archivo
            </button>
          </div>
        )}
      </div>

      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Busca por IMEI, empresa, contacto, cotización o modelo…"
        className="input w-full"
        aria-label="Buscar ventas"
      />

      {resumen && (
        <p className="text-xs text-zinc-500">
          {resumen.total_ventas.toLocaleString("es-MX")} ventas ·{" "}
          {resumen.total_equipos.toLocaleString("es-MX")} equipos ·{" "}
          {fechaMes(resumen.fecha_min)} – {fechaMes(resumen.fecha_max)}
          {resumen.ultima_carga_at && ` · última carga ${hace(resumen.ultima_carga_at)}`}
        </p>
      )}

      {loading ? (
        <div className="card space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 animate-pulse rounded bg-ink-800" />
          ))}
        </div>
      ) : errorCarga ? (
        <div className="card py-8 text-center space-y-2">
          <p className="text-sm text-zinc-400">No pude cargar las ventas. Revisa tu conexión.</p>
          <button onClick={() => void cargar(q, page)} className="btn-ghost text-sm">
            Reintentar
          </button>
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="card py-8 text-center">
          <p className="text-sm text-zinc-500">
            {q.trim()
              ? `No encontramos nada con «${q.trim()}». Prueba con menos texto o revisa el número.`
              : "Aún no hay ventas cargadas. El admin puede subir el Excel del CRM con «Subir archivo»."}
          </p>
        </div>
      ) : (
        <>
          {/* Escritorio y tablet: tabla. En tablet se esconde Contacto para no aplastarse. */}
          <div className="card hidden p-0 md:block overflow-x-auto">
            {/* table-fixed: los anchos los fijan los th y el truncate funciona
                sin que la tabla desborde la card en ningún ancho. */}
            <table className="w-full table-fixed text-sm">
              <thead>
                <tr className="border-b border-ink-700 text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="w-32 px-3 py-2 font-medium">Fecha</th>
                  <th className="w-[24%] px-3 py-2 font-medium">Empresa</th>
                  <th className="hidden w-[17%] px-3 py-2 font-medium lg:table-cell">Contacto</th>
                  <th className="px-3 py-2 font-medium">Concepto</th>
                  <th className="w-28 px-3 py-2 font-medium">Cotización</th>
                  <th className="w-20 px-3 py-2 text-right font-medium">Equipos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-700/60">
                {data.items.map((v) => (
                  <FilaVenta
                    key={v.id}
                    v={v}
                    expandida={expandida === v.id}
                    onToggle={() => setExpandida(expandida === v.id ? null : v.id)}
                    qCompacta={qCompacta}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Móvil: tarjetas */}
          <div className="space-y-2 md:hidden">
            {data.items.map((v) => (
              <TarjetaVenta
                key={v.id}
                v={v}
                expandida={expandida === v.id}
                onToggle={() => setExpandida(expandida === v.id ? null : v.id)}
                qCompacta={qCompacta}
              />
            ))}
          </div>

          {totalPaginas > 1 && (
            <div className="flex items-center justify-between text-sm">
              <button
                onClick={() => irAPagina(page - 1)}
                disabled={page <= 1}
                className="btn-ghost disabled:opacity-40"
              >
                ← Anterior
              </button>
              <span className="text-xs text-zinc-500">
                Página {page} de {totalPaginas} · {data.total.toLocaleString("es-MX")}{" "}
                resultado{data.total === 1 ? "" : "s"}
              </span>
              <button
                onClick={() => irAPagina(page + 1)}
                disabled={page >= totalPaginas}
                className="btn-ghost disabled:opacity-40"
              >
                Siguiente →
              </button>
            </div>
          )}
        </>
      )}

      {subirAbierto && (
        <SubirArchivoModal
          onClose={() => setSubirAbierto(false)}
          onTerminado={() => void alTerminarCarga()}
        />
      )}
      {cargasAbierto && (
        <CargasModal
          onClose={() => setCargasAbierto(false)}
          onRevertido={() => void alTerminarCarga()}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ fila */

function BadgeGarantia() {
  return (
    <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-300">
      Garantía
    </span>
  );
}

const ROL_LABEL: Record<IdentificadorOut["rol"], string | null> = {
  normal: null,
  falla: "falla",
  nuevo: "nuevo",
};

/** Chips de identificadores, copiables de un clic. El que coincide con la
 * búsqueda se resalta para encontrarlo entre 100 IMEIs de un vistazo. */
function ChipsIdentificadores({ v, qCompacta }: { v: VentaOut; qCompacta: string }) {
  if (v.identificadores.length === 0) {
    return <p className="text-xs text-zinc-600">Esta venta no trae equipos con número (licencias, accesorios o envío).</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {v.identificadores.map((i) => {
        const resaltado = qCompacta.length >= 4 && i.valor.toLowerCase().includes(qCompacta);
        const rol = ROL_LABEL[i.rol];
        return (
          <span
            key={`${i.valor}-${i.rol}`}
            className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-xs ${
              resaltado
                ? "border-accent-500 bg-accent-500/15 text-accent-200"
                : "border-ink-600 bg-ink-800 text-zinc-300"
            }`}
          >
            {rol && (
              <span
                className={`text-[9px] font-sans uppercase tracking-wide ${
                  i.rol === "falla" ? "text-red-400" : "text-emerald-400"
                }`}
              >
                {rol}
              </span>
            )}
            <CodigoCopiable valor={i.valor} etiqueta={i.tipo === "imei" ? "IMEI" : "número"}>
              {i.valor}
            </CodigoCopiable>
          </span>
        );
      })}
    </div>
  );
}

function DetalleVenta({ v, qCompacta }: { v: VentaOut; qCompacta: string }) {
  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-400">
        <span>
          Contacto: <span className="text-zinc-200">{contacto(v)}</span>
        </span>
        {v.cotizacion && (
          <span>
            Cotización:{" "}
            <CodigoCopiable valor={v.cotizacion} etiqueta="cotización" className="text-zinc-200">
              {v.cotizacion}
            </CodigoCopiable>
          </span>
        )}
      </div>
      {v.concepto && <p className="whitespace-pre-wrap text-zinc-300">{v.concepto}</p>}
      <ChipsIdentificadores v={v} qCompacta={qCompacta} />
      {v.observaciones && v.identificadores.length === 0 && (
        <p className="whitespace-pre-wrap break-words text-xs text-zinc-500">{v.observaciones}</p>
      )}
    </div>
  );
}

function FilaVenta({
  v,
  expandida,
  onToggle,
  qCompacta,
}: {
  v: VentaOut;
  expandida: boolean;
  onToggle: () => void;
  qCompacta: string;
}) {
  const equipos = contarEquipos(v);
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer transition hover:bg-ink-800/40"
        aria-expanded={expandida}
      >
        <td className="truncate px-3 py-2 text-zinc-400">{fecha(v.fecha_cierre)}</td>
        <td className="truncate px-3 py-2 font-medium text-zinc-200">
          {v.empresa || contacto(v)}
        </td>
        <td className="hidden truncate px-3 py-2 text-zinc-400 lg:table-cell">
          {contacto(v)}
        </td>
        <td className="px-3 py-2 text-zinc-300">
          <span className="flex items-center gap-2">
            <span className="truncate">{v.concepto || "—"}</span>
            {v.es_garantia && <BadgeGarantia />}
          </span>
        </td>
        <td className="truncate px-3 py-2 font-mono text-xs text-zinc-400">
          {v.cotizacion || "—"}
        </td>
        <td className="px-3 py-2 text-right text-zinc-400">
          {equipos > 0 ? equipos : "—"}
        </td>
      </tr>
      {expandida && (
        <tr className="bg-ink-800/20">
          <td colSpan={6} className="px-3 py-3">
            <DetalleVenta v={v} qCompacta={qCompacta} />
          </td>
        </tr>
      )}
    </>
  );
}

function TarjetaVenta({
  v,
  expandida,
  onToggle,
  qCompacta,
}: {
  v: VentaOut;
  expandida: boolean;
  onToggle: () => void;
  qCompacta: string;
}) {
  const equipos = contarEquipos(v);
  return (
    <div className="card space-y-1.5 p-3" onClick={onToggle} aria-expanded={expandida}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium text-zinc-200">
          {v.empresa || contacto(v)}
        </span>
        <span className="shrink-0 text-xs text-zinc-500">{fecha(v.fecha_cierre)}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <span className="truncate">{v.concepto || "—"}</span>
        {v.es_garantia && <BadgeGarantia />}
      </div>
      {!expandida && (
        <p className="text-[11px] text-zinc-600">
          {equipos > 0
            ? `${equipos} equipo${equipos === 1 ? "" : "s"}`
            : v.identificadores.length > 0
              ? `${v.identificadores.length} número${v.identificadores.length === 1 ? "" : "s"}`
              : "sin equipos con número"}
          {v.cotizacion && ` · cot. ${v.cotizacion}`}
          {" · toca para ver el detalle"}
        </p>
      )}
      {expandida && (
        <div className="border-t border-ink-700 pt-2" onClick={(e) => e.stopPropagation()}>
          <DetalleVenta v={v} qCompacta={qCompacta} />
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------- subir archivo */

function SubirArchivoModal({
  onClose,
  onTerminado,
}: {
  onClose: () => void;
  onTerminado: () => void;
}) {
  const toast = useToast();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [dragActivo, setDragActivo] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [resultado, setResultado] = useState<CargaResumen | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function elegir(f: File | undefined | null) {
    setError(null);
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      setError("Solo se acepta el Excel del CRM en formato .xlsx.");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setError("El archivo pesa más de 20 MB — no parece el Excel de ventas.");
      return;
    }
    setArchivo(f);
  }

  async function subir() {
    if (!archivo || subiendo) return;
    setSubiendo(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", archivo);
      const res = await api.postForm<CargaResumen>("/api/ventas/cargas", fd);
      setResultado(res);
      toast.success(
        "Archivo cargado",
        `${res.filas_nuevas.toLocaleString("es-MX")} venta${res.filas_nuevas === 1 ? "" : "s"} nueva${res.filas_nuevas === 1 ? "" : "s"}`,
      );
      onTerminado();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail);
      } else {
        // Red caída a medio POST: no sabemos si alcanzó a aplicarse, pero
        // re-subir es seguro por diseño — díselo al usuario.
        setError(
          "Se perdió la conexión y no sé si la carga alcanzó a aplicarse. " +
            "Vuelve a subir el MISMO archivo: lo ya cargado no se duplica.",
        );
      }
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <Modal
      open={true}
      onOpenChange={(v) => {
        if (!v && !subiendo) onClose();
      }}
      title="Subir archivo de ventas"
      description="El Excel que manda el CRM (.xlsx). Puedes subir el archivo completo otra vez: lo ya cargado no se duplica."
      size="md"
      footer={
        resultado ? (
          <button onClick={onClose} className="btn-primary">
            Listo
          </button>
        ) : (
          <>
            <button onClick={onClose} disabled={subiendo} className="btn-ghost disabled:opacity-40">
              Cancelar
            </button>
            <button
              onClick={() => void subir()}
              disabled={!archivo || subiendo}
              className="btn-primary disabled:opacity-50"
            >
              {subiendo ? "Cargando…" : "Cargar ventas"}
            </button>
          </>
        )
      }
    >
      {resultado ? (
        <div className="space-y-2 text-sm">
          <p className="text-zinc-200">
            ✅ {resultado.filas_nuevas.toLocaleString("es-MX")} venta
            {resultado.filas_nuevas === 1 ? "" : "s"} nueva
            {resultado.filas_nuevas === 1 ? "" : "s"} ·{" "}
            {resultado.filas_duplicadas.toLocaleString("es-MX")} ya estaba
            {resultado.filas_duplicadas === 1 ? "" : "n"} ·{" "}
            {resultado.identificadores_nuevos.toLocaleString("es-MX")} equipos/números nuevos
          </p>
          {resultado.filas_avisos > 0 && (
            <div className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              <p className="mb-1">
                {resultado.filas_avisos} fila{resultado.filas_avisos === 1 ? "" : "s"} con avisos:
              </p>
              <ul className="list-inside list-disc space-y-0.5">
                {resultado.avisos_detalle.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              if (!subiendo) setDragActivo(true);
            }}
            onDragLeave={() => setDragActivo(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActivo(false);
              if (!subiendo) elegir(e.dataTransfer.files?.[0]);
            }}
            onClick={() => !subiendo && inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !subiendo) {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            aria-label="Arrastra aquí el Excel o haz clic para elegirlo"
            className={`flex min-h-[110px] cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-6 text-center transition
              ${dragActivo ? "border-accent-500 bg-accent-500/10" : "border-ink-600 hover:border-ink-500"}
              ${subiendo ? "pointer-events-none opacity-60" : ""}`}
          >
            {archivo ? (
              <>
                <p className="text-sm text-zinc-200">{archivo.name}</p>
                <p className="text-xs text-zinc-500">
                  {fmtBytes(archivo.size)}
                </p>
                {!subiendo && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setArchivo(null);
                      if (inputRef.current) inputRef.current.value = "";
                    }}
                    className="btn-ghost text-xs"
                  >
                    Quitar
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-zinc-300">
                  Arrastra aquí el Excel (.xlsx)
                </p>
                <p className="text-xs text-zinc-500">o haz clic para elegirlo</p>
              </>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => elegir(e.target.files?.[0])}
          />
          {subiendo && (
            <p className="text-xs text-zinc-400">
              Cargando y revisando el archivo… no cierres esta ventana.
            </p>
          )}
          {error && (
            <div className="whitespace-pre-wrap rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              {error}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/* -------------------------------------------------------------- cargas */

function CargasModal({
  onClose,
  onRevertido,
}: {
  onClose: () => void;
  onRevertido: () => void;
}) {
  const toast = useToast();
  const [cargas, setCargas] = useState<CargaOut[] | null>(null);
  const [confirmar, setConfirmar] = useState<CargaOut | null>(null);

  const cargar = useCallback(async () => {
    try {
      setCargas(await api.get<CargaOut[]>("/api/ventas/cargas"));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar el historial", msg);
    }
  }, [toast]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function revertir(c: CargaOut) {
    try {
      const res = await api.del<{ ventas_borradas: number }>(`/api/ventas/cargas/${c.id}`);
      toast.success(
        "Carga revertida",
        `${res.ventas_borradas.toLocaleString("es-MX")} venta${res.ventas_borradas === 1 ? "" : "s"} borrada${res.ventas_borradas === 1 ? "" : "s"}`,
      );
      await cargar();
      onRevertido();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude revertir la carga", msg);
    }
  }

  const fmtCargaFecha = new Intl.DateTimeFormat("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title="Cargas"
      description="Cada archivo subido y lo que metió. Revertir una carga borra SOLO las ventas que esa carga insertó como nuevas."
      size="lg"
      footer={
        <button onClick={onClose} className="btn-ghost">
          Cerrar
        </button>
      }
    >
      {cargas === null ? (
        <p className="text-sm text-zinc-500">Cargando…</p>
      ) : cargas.length === 0 ? (
        <p className="text-sm text-zinc-500">Aún no se ha subido ningún archivo.</p>
      ) : (
        <div className="divide-y divide-ink-700">
          {cargas.map((c) => (
            <div key={c.id} className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-center sm:gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-zinc-200">{c.filename}</p>
                <p className="text-xs text-zinc-500">
                  {fmtCargaFecha.format(new Date(c.created_at))} · {c.subido_por} ·{" "}
                  <span className="text-zinc-400">
                    {c.filas_nuevas.toLocaleString("es-MX")} nuevas
                  </span>{" "}
                  / {c.filas_duplicadas.toLocaleString("es-MX")} repetidas
                  {c.filas_avisos > 0 && ` · ${c.filas_avisos} avisos`}
                </p>
              </div>
              {/* Qué hace lo dice la descripción de esta ventana y el aviso de
                  confirmar (antes, además, un tooltip). */}
              <button
                onClick={() => setConfirmar(c)}
                className="btn-ghost self-start text-xs text-red-400/80 hover:text-red-300 sm:self-auto"
              >
                Revertir
              </button>
            </div>
          ))}
        </div>
      )}

      {confirmar && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmar(null)}
          title="Revertir carga"
          description={`Se borrarán las ${confirmar.filas_nuevas.toLocaleString("es-MX")} ventas que metió «${confirmar.filename}» (${fmtCargaFecha.format(new Date(confirmar.created_at))}). Las ventas de otras cargas no se tocan. Si te equivocas, puedes volver a subir el archivo.`}
          confirmLabel="Revertir carga"
          variant="danger"
          onConfirm={() => revertir(confirmar)}
        />
      )}
    </Modal>
  );
}
